import React, { useState } from 'react'
import {
  useGetOnboardingStatsQuery,
  useGetOnboardingTableQuery,
  useGetTrialActivityQuery,
  useDeleteTrialActivityForUserMutation,
  OnboardingTableRow,
  TrialActivityEntry,
} from '../store/api/onboardingApi'
import { formatDateTimeEST } from '../utils/dateUtils'

/** Map action keys to human-readable labels */
const ACTION_LABELS: Record<string, string> = {
  trial_session_started: 'Started trial session',
  rfp_creation_started: 'Started RFP creation',
  pipeline_generation_seen: 'Saw pipeline generating',
  pipeline_generation_completed: 'Pipeline generation completed',
  pipeline_reveal_popup_accepted: 'Accepted pipeline reveal popup',
  pipeline_time_saved_popup_accepted: 'Accepted "time saved" popup',
  new_project_clicked: 'Clicked New Project',
  new_rfp_input_started: 'Opened new RFP input',
  org_project_named: 'Named org and project',
  questions_answered: 'Answered questions',
  team_size_indicated: 'Indicated team size',
  rfp_submitted: 'Submitted RFP',
  own_pipeline_generation_seen: 'Saw own project pipeline generating',
  own_pipeline_reveal_popup_accepted: 'Accepted own pipeline popup',
  name_password_added: 'Added name and password (registered)',
  project_summary_clicked: 'Clicked Project Summary',
  source_brief_clicked: 'Clicked Source Brief',
  est_effort_clicked: 'Clicked Est. Effort',
  billing_status_clicked: 'Clicked Billing Status',
  billing_status_changed: 'Changed billing status',
  est_price_clicked: 'Clicked Est. Price',
  pipeline_tab_clicked: 'Clicked Pipeline tab',
  timeline_tab_clicked: 'Clicked Timeline tab',
  team_tab_clicked: 'Clicked Team tab',
  proposal_tab_clicked: 'Clicked Proposal tab',
  export_menu_opened: 'Opened Export menu',
  export_asana_clicked: 'Clicked Export → Asana',
  export_trello_clicked: 'Clicked Export → Trello',
  export_gantt_xlsx_clicked: 'Clicked Export → Gantt XLSX',
  export_word_docx_clicked: 'Clicked Export → Word DOCX',
  export_ms_project_clicked: 'Clicked Export → MS Project',
  export_monday_clicked: 'Clicked Export → Monday.com',
  profile_menu_clicked: 'Opened Profile (user menu)',
  collaborators_menu_clicked: 'Opened Collaborators (user menu)',
  billing_menu_clicked: 'Opened Billing (user menu)',
  signout_clicked: 'Clicked Sign Out',
  user_menu_expanded: 'Expanded user menu',
  pipeline_task_clicked: 'Clicked a pipeline task',
  pipeline_task_expanded: 'Expanded a pipeline task',
  pipeline_drag_drop: 'Dragged/dropped a pipeline task',
  pipeline_zoom_changed: 'Changed pipeline zoom',
  pipeline_ai_chat_opened: 'Opened Architect Assistant chat',
  pipeline_ai_message_sent: 'Sent AI chat message',
  pipeline_reshuffle_requested: 'Requested pipeline reshuffle',
  timeline_task_clicked: 'Clicked a timeline task',
  timeline_filter_changed: 'Changed timeline filter',
  timeline_zoom_changed: 'Changed timeline zoom',
  team_member_clicked: 'Clicked a team member',
  team_member_added: 'Added new team member',
  team_member_removed: 'Removed team member',
  team_member_restored: 'Restored team member (undo)',
  team_member_new_removed: 'Removed unsaved new member',
  team_step_toggled: 'Toggled activated step',
  team_working_day_toggled: 'Toggled working day',
  team_hours_changed: 'Changed hours per day',
  team_start_date_changed: 'Changed member start date',
  team_start_date_cleared: 'Cleared member start date',
  team_changes_published: 'Published team changes',
  team_add_collaborator_clicked: 'Clicked Add Collaborator',
  team_collaborator_invited: 'Sent collaborator invitation',
  proposal_viewed: 'Viewed Proposal',
  proposal_prd_tab_clicked: 'Clicked PRD tab (Proposal)',
  proposal_sow_tab_clicked: 'Clicked SOW tab (Proposal)',
  proposal_delivery_plan_tab_clicked: 'Clicked Delivery Plan tab (Proposal)',
  proposal_sow_pricing_button_clicked: 'Clicked pricing button in SOW',
  proposal_edit_started: 'Started editing proposal doc',
  proposal_edit_saved: 'Saved proposal doc edit',
  proposal_download_pdf_clicked: 'Downloaded proposal as PDF',
  proposal_download_docx_clicked: 'Downloaded proposal as DOCX',
  leave_feedback_clicked: 'Clicked Leave Feedback',
  feedback_submitted: 'Submitted feedback',
  session_closed: 'Closed session',
  resources_box_shown: 'Resources box appeared',
  resources_email_typing_started: 'Started typing email in resources box',
  resources_email_requested: 'Submitted resources email',
  generate_own_plan_button_clicked: 'Clicked "Generate your own plan"',
  own_plan_email_typing_started: 'Started typing email for own plan',
  own_plan_email_submitted: 'Submitted email for own plan',
  trial_resources_link_visited: 'Visited trial resources email link',
  trial_project_switched: 'Switched project in left panel',
  effort_weeks_changed: 'Changed est. effort weeks',
  effort_start_date_changed: 'Changed est. effort start date',
  effort_ticket_hours_changed: 'Changed ticket hours in effort editor',
  effort_entry_deleted: 'Deleted entry in effort editor',
  effort_changes_published: 'Published effort changes',
  pricing_model_selected: 'Selected pricing/billing structure',
  pricing_saved: 'Saved pricing',
  onboarding_question_answered: 'Answered onboarding question',
  onboarding_team_size_changed: 'Changed team size in onboarding',
  onboarding_additional_context_typed: 'Typed additional context in onboarding',
  onboarding_generate_pipeline_clicked: 'Clicked Generate Pipeline',
  profile_name_saved: 'Saved profile name',
  profile_password_changed: 'Changed profile password',
  billing_tab_clicked: 'Clicked billing tab',
  collaborators_invite_resent: 'Resent collaborator invitation',
  collaborators_invite_cancelled: 'Cancelled/removed collaborator',
  collaborators_seats_purchase_clicked: 'Clicked purchase collaborator seats',
  credits_purchased: 'Purchased project credits',
  credit_used_to_unlock: 'Used credit to unlock project',
  collaborator_seats_purchased: 'Purchased collaborator seats',
  collaborators_page_viewed: 'Viewed Collaborators page',
  collaborator_invited: 'Sent collaborator invite',
  billing_page_viewed: 'Viewed Billing & Plan page',
  billing_checkout_started: 'Started billing checkout (Stripe)',
  billing_payment_method_clicked: 'Clicked Add/Change payment method',
  billing_collaborators_link_clicked: 'Clicked Manage Collaborators (from Billing)',
  collab_payment_nav_clicked: 'Clicked Pay-with-card → went to Payment Methods (Collaborators)',
  collab_add_payment_method_clicked: 'Clicked "Add a payment method first" (Collaborators)',
  creditgate_tier_selected: 'Selected credit tier (credit gate)',
  creditgate_dismissed: 'Dismissed credit gate → back to pipeline',
  billing_credit_tier_selected: 'Selected credit tier (Billing panel)',
  billing_add_payment_method_clicked: 'Clicked "Add payment method" (Billing)',
  billing_use_different_card_clicked: 'Clicked "Use a different card" (Billing)',
  billing_seat_purchase_clicked: 'Clicked Purchase seats button (Billing)',
  billing_seats_removed: 'Removed collaborator seats (Billing)',
  billing_payment_method_removed: 'Removed a payment method (Billing)',
  billing_add_card_clicked: 'Clicked "+ Add Card" (Billing)',
  billing_invoice_pdf_clicked: 'Opened invoice PDF (Billing)',
  billing_invoice_view_clicked: 'Opened invoice details (Billing)',
  organization_page_clicked: 'Opened Organizations page (sidebar)',
  org_renamed: 'Renamed organization',
  org_deleted: 'Deleted organization',
  org_project_deleted: 'Deleted project (from Organizations)',
  org_created: 'Created new organization',
  org_switched: 'Switched active organization',
  // Mo Check portal (/mo-portal)
  portal_opened: 'Opened Mo portal',
  nav_overview: 'Mo: navigated to Overview',
  nav_decisions: 'Mo: navigated to Decisions',
  nav_checks: 'Mo: navigated to Checks',
  nav_integrations: 'Mo: navigated to Integrations',
  nav_billing: 'Mo: navigated to Billing',
  nav_collaborators: 'Mo: navigated to Collaborators',
  nav_help: 'Mo: navigated to Help',
  header_nav_integrations: 'Mo header: opened Integrations',
  header_nav_decisions: 'Mo header: opened Decisions',
  header_nav_checks: 'Mo header: opened Checks',
  header_mobile_sidebar_opened: 'Mo: opened mobile sidebar',
  check_project_switched: 'Mo: switched project',
  check_signout_clicked: 'Mo: signed out',
  new_project_modal_opened: 'Mo: opened New Project dialog',
  checkout_cancelled: 'Mo: cancelled Stripe checkout',
  git_oauth_error: 'Mo: Git OAuth error',
  repo_select_modal_opened: 'Mo: opened Choose repositories',
  repo_select_modal_saved: 'Mo: saved repository selection',
  check_integrations_repos_saved: 'Mo Integrations: saved repos',
  check_git_oauth_started: 'Mo: started Git OAuth',
  check_git_disconnected: 'Mo: disconnected Git',
  check_slack_install_started: 'Mo: started Slack install',
  check_slack_disconnected: 'Mo: disconnected Slack',
  check_continue_email_clicked: 'Mo: Continue with email (integrations banner)',
  check_collaborator_invited: 'Mo: invited collaborator',
  check_collaborator_invite_resent: 'Mo: resent collaborator invite',
  check_collaborator_removed: 'Mo: removed collaborator',
  check_collaborator_seats_purchased: 'Mo: purchased collaborator seats',
  check_collaborator_seats_reduced: 'Mo: scheduled collaborator seat reduction',
  check_collaborator_seat_removal_cancelled: 'Mo: cancelled scheduled seat removal',
  check_billing_cancel_subscription: 'Mo Billing: cancelled subscription',
  check_billing_project_unsubscribe_scheduled: 'Mo Billing: scheduled project removal',
  check_billing_undo_project_unsubscribe: 'Mo Billing: undid project removal',
  check_billing_keep_collaborator_seats: 'Mo Billing: kept collaborator seats',
  check_billing_reactivate: 'Mo Billing: reactivated subscription',
  check_billing_start_trial_existing_card: 'Mo Billing: started trial with saved card',
  check_billing_subscribe_project: 'Mo Billing: subscribed project',
  check_billing_payment_method_removed: 'Mo Billing: removed payment method',
  check_billing_add_card_opened: 'Mo Billing: opened add card',
  check_billing_trial_card_submitted: 'Mo Billing: submitted card for trial',
  check_billing_project_subscribe_card: 'Mo Billing: subscribed project with new card',
  check_help_contact_opened: 'Mo Help: opened contact dialog',
  check_help_contact_submitted: 'Mo Help: sent contact message',
  check_slack_command_copied: 'Mo: copied Slack approve command',
  check_sample_check_run: 'Mo: ran sample PR check',
  check_sample_check_mode_changed: 'Mo: changed sample check mode',
  check_overview_email_submitted: 'Mo Overview: submitted email',
  check_checkout_started: 'Mo: started checkout',
  check_dashboard_email_submitted: 'Mo Dashboard: submitted email',
  dashboard_nav_checks: 'Mo Dashboard: went to Checks',
  dashboard_nav_decisions: 'Mo Dashboard: went to Decisions',
  dashboard_nav_integrations: 'Mo Dashboard: went to Integrations',
  dashboard_nav_billing: 'Mo Dashboard: went to Billing',
  dashboard_nav_collaborators: 'Mo Dashboard: went to Collaborators',
  dashboard_recent_decision_clicked: 'Mo Dashboard: clicked recent decision',
  dashboard_recent_check_clicked: 'Mo Dashboard: clicked recent PR check',
  dashboard_setup_manage_integrations: 'Mo Dashboard: Manage integrations (setup card)',
  check_overview_goto_integrations: 'Mo Overview: went to Integrations',
  check_overview_goto_decisions: 'Mo Overview: went to Decisions',
  check_overview_choose_repos_clicked: 'Mo Overview: clicked Choose repositories',
  check_integrations_edit_repos_clicked: 'Mo Integrations: clicked Edit repositories',
  project_renamed: 'Mo: renamed project',
  project_created: 'Mo: created project',
  check_decision_status_changed: 'Mo Decisions: changed decision status',
  check_decision_deleted: 'Mo Decisions: deleted decision',
  check_decision_created: 'Mo Decisions: added decision',
  check_decision_add_dialog_opened: 'Mo Decisions: opened Add decision',
  check_decisions_view: 'Mo Decisions: changed view',
  check_document_upload_prompt_clicked: 'Mo Decisions: opened document upload',
  check_document_uploaded: 'Mo Decisions: uploaded document',
  check_document_rules_approved: 'Mo Decisions: approved extracted rules',
  check_document_rules_rejected: 'Mo Decisions: rejected extracted rules',
  check_document_rule_edited: 'Mo Decisions: edited extracted rule',
  check_conflict_goto_decisions: 'Mo Checks: opened linked decisions',
  check_pr_run_expanded: 'Mo Checks: expanded PR run',
  check_pr_run_collapsed: 'Mo Checks: collapsed PR run',
  check_analytics_page_viewed: 'Mo Analytics: viewed Checks Analytics page',
  check_analytics_time_range_changed: 'Mo Analytics: changed time range',
  check_analytics_open_overrides_clicked: 'Mo Analytics: opened Overrides from summary',
  check_help_page_viewed: 'Mo Help: viewed Help Center',
  check_help_tab_changed: 'Mo Help: changed tab',
  check_help_search_result_opened: 'Mo Help: opened search result',
  check_help_replay_walkthrough_clicked: 'Mo Help: replayed walkthrough',
  check_help_explore_walkthrough_clicked: 'Mo Help: started tips tour',
  check_contact_support_page_viewed: 'Mo Help: viewed Contact Support page',
  check_pause_banner_email_cta: 'Mo Checks: paused banner → email',
  check_pause_banner_billing_cta: 'Mo Checks: paused banner → billing',
  check_conflict_notify_toggled: 'Mo Checks: toggled conflict emails',
  check_conflict_notify_needs_email: 'Mo Checks: conflict emails need email first',
  check_jira_oauth_started: 'Mo Integrations: started Jira OAuth',
  check_jira_disconnected: 'Mo Integrations: disconnected Jira',
  check_azure_devops_oauth_started: 'Mo Integrations: started Azure DevOps OAuth',
  check_azure_devops_disconnected: 'Mo Integrations: disconnected Azure DevOps',
  check_linear_oauth_started: 'Mo Integrations: started Linear OAuth',
  check_linear_disconnected: 'Mo Integrations: disconnected Linear',
  check_pending_invite_review_clicked: 'Mo: reviewed pending invite',
  check_pending_invite_dismissed: 'Mo: dismissed pending invite (Not now)',
  check_org_name_password_set: 'Mo: set name/password after verify',
  check_org_name_google_linked: 'Mo: linked Google after verify',
  mo_invite_accepted_logged_in: 'Mo invite: accepted (logged in)',
  mo_invite_registered_password: 'Mo invite: registered with password',
  mo_invite_registered_google: 'Mo invite: registered with Google',
  mo_invite_declined: 'Mo invite: declined',
  mo_invite_signout: 'Mo invite page: signed out',
  organization_modal_opened: 'Mo: opened Organizations modal',
  profile_modal_opened: 'Mo: opened Profile modal',
  /** Server-logged: Slack / portal decision appeared in Mo */
  mo_decision_recorded: 'Mo: decision recorded',
  /** Server-logged: PR/MR validation finished (pass / fail / warning / blocked) */
  mo_pr_check_completed: 'Mo: PR check completed',
  mo_client_error: 'Mo: client error',
  nav_settings_project: 'Mo: navigated to Project settings',
  nav_settings_organization: 'Mo: navigated to Organization settings',
  nav_settings_profile: 'Mo: navigated to Profile settings',
  'nav_help.contact_from_footer': 'Mo: Contact us (footer)',
}

function formatActionLabel(entry: TrialActivityEntry): string {
  const base = ACTION_LABELS[entry.action] || entry.action
  const m = entry.metadata as Record<string, unknown> | null
  if (entry.action === 'billing_status_changed' && m?.newStatus) {
    return `${base} → ${m.newStatus}`
  }
  if (entry.action === 'pipeline_ai_message_sent' && m?.message) {
    return `${base}: "${String(m.message).slice(0, 80)}${String(m.message).length > 80 ? '…' : ''}"`
  }
  if (entry.action === 'effort_weeks_changed' && m?.weeks != null) {
    return `${base}: ${m.weeks} weeks`
  }
  if (entry.action === 'effort_start_date_changed' && m?.date) {
    return `${base}: ${m.date}`
  }
  if (entry.action === 'effort_entry_deleted' && m?.entityType) {
    return `${base} (${m.entityType})`
  }
  if (entry.action === 'pricing_model_selected' && m?.label) {
    return `${base}: ${m.label}`
  }
  if (entry.action === 'pricing_saved' && m?.model) {
    const modelLabel = m.model === 'fixed' ? 'Fixed price' : m.model === 'tm' ? 'Time & materials' : 'Milestone'
    const detail = m.fixedPrice ? ` $${m.fixedPrice}` : m.hourlyRate ? ` $${m.hourlyRate}/hr` : m.milestoneTotal ? ` $${m.milestoneTotal}` : ''
    return `${base}: ${modelLabel}${detail}`
  }
  if (entry.action === 'own_plan_email_submitted' && m?.email) {
    return `${base}: ${m.email}`
  }
  if (entry.action === 'effort_changes_published') {
    const parts: string[] = []
    if (m?.weeksChanged) parts.push('weeks changed')
    if (m?.startDateChanged) parts.push('start date changed')
    if (m?.deletedCount) parts.push(`${m.deletedCount} deleted`)
    return parts.length > 0 ? `${base} (${parts.join(', ')})` : base
  }
  if (entry.action === 'onboarding_question_answered' && m?.question) {
    return `${base}: "${m.answer}" for "${String(m.question).slice(0, 50)}"`
  }
  if (entry.action === 'onboarding_team_size_changed' && m?.teamSize != null) {
    return `${base}: ${m.teamSize} people`
  }
  if (entry.action === 'billing_tab_clicked' && m?.tab) {
    return `${base}: ${m.tab}`
  }
  if ((entry.action === 'proposal_edit_started' || entry.action === 'proposal_edit_saved' || entry.action === 'proposal_download_pdf_clicked' || entry.action === 'proposal_download_docx_clicked') && m?.docType) {
    const docLabel = m.docType === 'prd' ? 'PRD' : m.docType === 'sow' ? 'SOW' : 'Delivery Plan'
    return `${base} (${docLabel})`
  }
  if (entry.action === 'trial_project_switched' && m?.projectName) {
    return `${base}: ${m.projectName}`
  }
  if (entry.action === 'collaborators_invite_resent' && m?.email) {
    return `${base}: ${m.email}`
  }
  if (entry.action === 'collaborators_invite_cancelled' && m?.email) {
    return `${base}: ${m.email}`
  }
  if (entry.action === 'collaborators_seats_purchase_clicked' && m?.qty != null) {
    return `${base}: ${m.qty} seat${Number(m.qty) > 1 ? 's' : ''}`
  }
  if (entry.action === 'credits_purchased' && m?.credits != null) {
    const src = m.source === 'creditgate' ? ' (credit gate)' : m.source === 'billing_panel' ? ' (billing panel)' : ''
    return `${base}: ${m.credits} credit${Number(m.credits) > 1 ? 's' : ''}${src}`
  }
  if (entry.action === 'credit_used_to_unlock' && m?.target) {
    return `${base} (${m.target})`
  }
  if (entry.action === 'collaborator_seats_purchased' && m?.qty != null) {
    const src = m.source === 'collaborators_page' ? ' (Collaborators page)' : m.source === 'billing_panel' ? ' (billing panel)' : ''
    return `${base}: ${m.qty} seat${Number(m.qty) > 1 ? 's' : ''}${src}`
  }
  if (entry.action === 'collaborator_invited' && m?.projectCount != null) {
    return `${base} (${m.projectCount} project${Number(m.projectCount) > 1 ? 's' : ''})`
  }
  if (entry.action === 'billing_checkout_started' && m?.credits != null) {
    return `${base}: ${m.credits} credit${Number(m.credits) > 1 ? 's' : ''}`
  }
  if (entry.action === 'creditgate_tier_selected' && m?.credits != null) {
    return `${base}: ${m.credits} credit${Number(m.credits) > 1 ? 's' : ''} ($${m.price})`
  }
  if (entry.action === 'billing_credit_tier_selected' && m?.credits != null) {
    const action = m.selected ? 'selected' : 'deselected'
    return `${base}: ${m.credits} credit${Number(m.credits) > 1 ? 's' : ''} (${action})`
  }
  if (entry.action === 'billing_seat_purchase_clicked' && m?.qty != null) {
    return `${base}: ${m.qty} seat${Number(m.qty) > 1 ? 's' : ''}`
  }
  if (entry.action === 'billing_seats_removed' && m?.qty != null) {
    return `${base}: ${m.qty} seat${Number(m.qty) > 1 ? 's' : ''}`
  }
  if (entry.action === 'billing_add_payment_method_clicked' && m?.source) {
    const srcLabel = m.source === 'credits_tab' ? 'Credits tab' : m.source === 'seats_tab' ? 'Seats tab' : String(m.source)
    return `${base} (${srcLabel})`
  }
  if (entry.action === 'billing_use_different_card_clicked' && m?.source) {
    const srcLabel = m.source === 'creditgate' ? 'credit gate' : m.source === 'credits_tab' ? 'Credits tab' : String(m.source)
    return `${base} (${srcLabel})`
  }
  if ((entry.action === 'billing_invoice_pdf_clicked' || entry.action === 'billing_invoice_view_clicked') && m?.invoiceId) {
    return `${base} #${m.invoiceId}`
  }
  if (entry.action === 'org_renamed' && m?.newName) {
    return `${base}: "${m.newName}"`
  }
  if (entry.action === 'org_deleted' && m?.orgName) {
    return `${base}: "${m.orgName}"`
  }
  if (entry.action === 'org_project_deleted' && m?.projectName) {
    return `${base}: "${m.projectName}"`
  }
  if (entry.action === 'org_created' && m?.orgName) {
    return `${base}: "${m.orgName}"`
  }
  if (entry.action === 'org_switched' && m?.orgName) {
    return `${base}: "${m.orgName}"`
  }
  if ((entry.action === 'team_member_removed' || entry.action === 'team_member_restored' || entry.action === 'team_member_new_removed' || entry.action === 'team_start_date_cleared') && m?.memberName) {
    return `${base}: ${m.memberName}`
  }
  if (entry.action === 'team_step_toggled' && m?.memberName) {
    const toggled = m.active ? 'enabled' : 'disabled'
    return `${base}: ${m.memberName} — ${m.step} ${toggled}`
  }
  if (entry.action === 'team_working_day_toggled' && m?.memberName) {
    const toggled = m.active ? 'added' : 'removed'
    return `${base}: ${m.memberName} — ${m.day} ${toggled}`
  }
  if (entry.action === 'team_hours_changed' && m?.memberName) {
    return `${base}: ${m.memberName} → ${m.hoursPerDay} hrs/day`
  }
  if (entry.action === 'team_start_date_changed' && m?.memberName) {
    return `${base}: ${m.memberName} → ${m.date}`
  }
  if (entry.action === 'team_changes_published') {
    const parts: string[] = []
    if (m?.removedCount) parts.push(`${m.removedCount} removed`)
    if (m?.addedCount) parts.push(`${m.addedCount} added`)
    if (m?.stepChanges) parts.push(`${m.stepChanges} step change${Number(m.stepChanges) > 1 ? 's' : ''}`)
    if (m?.scheduleChanges) parts.push(`${m.scheduleChanges} schedule change${Number(m.scheduleChanges) > 1 ? 's' : ''}`)
    return parts.length > 0 ? `${base} (${parts.join(', ')})` : base
  }
  if (entry.action === 'git_oauth_error' && m?.error) {
    return `${base}: ${String(m.error)}`
  }
  if (entry.action === 'check_decisions_view' && m?.view) {
    return `${base}: ${m.view}`
  }
  if (entry.action === 'check_document_uploaded' && (m?.fileName || m?.documentUuid)) {
    const name = m?.fileName ? `"${String(m.fileName)}"` : ''
    const doc = m?.documentUuid ? ` …${String(m.documentUuid).slice(0, 8)}` : ''
    return `${base}${name ? `: ${name}` : ''}${doc}`
  }
  if (entry.action === 'check_document_rules_approved' && (m?.approved != null || m?.selectedCount != null)) {
    const approved = m?.approved != null ? `${Number(m.approved)} approved` : null
    const selected = m?.selectedCount != null ? `${Number(m.selectedCount)} selected` : null
    const parts = [approved, selected].filter(Boolean).join(' · ')
    return parts ? `${base}: ${parts}` : base
  }
  if (entry.action === 'check_document_rules_rejected' && m?.rejectedCount != null) {
    return `${base}: ${Number(m.rejectedCount)} rejected`
  }
  if (entry.action === 'check_document_rule_edited' && (m?.ruleUuid || m?.documentUuid)) {
    const doc = m?.documentUuid ? `doc …${String(m.documentUuid).slice(0, 8)}` : null
    const rule = m?.ruleUuid ? `rule …${String(m.ruleUuid).slice(0, 8)}` : null
    const parts = [doc, rule].filter(Boolean).join(' · ')
    return parts ? `${base} (${parts})` : base
  }
  if (entry.action === 'repo_select_modal_opened' && m?.source) {
    return `${base} (${m.source}${m.provider ? ` · ${m.provider}` : ''})`
  }
  if (entry.action === 'check_sample_check_run' && m?.mode) {
    return `${base}: ${m.mode}`
  }
  if (entry.action === 'check_git_oauth_started' && m?.source) {
    return `${base} (${m.provider}, ${m.source})`
  }
  if (entry.action === 'check_conflict_notify_toggled' && m?.enabled != null) {
    return `${base}: ${m.enabled ? 'on' : 'off'}`
  }
  if (entry.action === 'project_renamed' && m?.name) {
    return `${base}: "${String(m.name)}"`
  }
  if (entry.action === 'project_created' && m?.name) {
    return `${base}: "${String(m.name)}"`
  }
  if (
    (entry.action === 'check_overview_goto_integrations' || entry.action === 'check_overview_goto_decisions') &&
    m?.source
  ) {
    return `${base} (${String(m.source).replace(/_/g, ' ')})`
  }
  if (entry.action === 'check_overview_choose_repos_clicked' && m?.provider) {
    return `${base}: ${m.provider}`
  }
  if (entry.action === 'dashboard_recent_check_clicked' && m?.pr_number != null) {
    return `${base} (PR #${m.pr_number})`
  }
  if (entry.action === 'check_integrations_edit_repos_clicked' && m?.provider) {
    return `${base}: ${m.provider}`
  }
  if (entry.action === 'check_project_switched' && m?.projectUuid) {
    const short = String(m.projectUuid).slice(0, 8)
    return `${base} (…${short})`
  }
  if (entry.action === 'mo_decision_recorded' && m) {
    const src = m.source === 'slack' ? 'Slack' : m.source === 'web' ? 'Portal' : String(m.source || '')
    const st = m.status != null ? String(m.status) : ''
    const title = m.title ? String(m.title).slice(0, 72) : ''
    const bits = [src, st, title].filter(Boolean)
    return bits.length ? `${base} (${bits.join(' · ')})` : base
  }
  if (entry.action === 'mo_pr_check_completed' && m) {
    const prov = m.provider ? String(m.provider) : 'git'
    const pr = m.pr_number != null ? `PR #${m.pr_number}` : 'PR'
    const st = m.status ? String(m.status) : ''
    const n = m.issues_count != null ? Number(m.issues_count) : 0
    const issueBit = n > 0 ? ` · ${n} issue${n === 1 ? '' : 's'}` : ''
    return `${base}: ${prov} ${pr} → ${st}${issueBit}`
  }
  if (entry.action === 'mo_client_error' && m) {
    const msg = m.message ? String(m.message).slice(0, 160) : m.kind ? String(m.kind) : ''
    const place = m.subPage || m.view ? [m.subPage, m.view].filter(Boolean).join(' · ') : ''
    const bits = [msg, place].filter(Boolean)
    return bits.length ? `Mo: error — ${bits.join(' · ')}` : base
  }
  return base
}

interface ActivityModalProps {
  userId: number | null
  userEmail: string | null
  onClose: () => void
}

function ActivityModal({ userId, userEmail, onClose }: ActivityModalProps) {
  const { data, isLoading, isError, refetch } = useGetTrialActivityQuery(userId!, { skip: userId === null })
  const [deleteAllForUser, { isLoading: isDeleting }] = useDeleteTrialActivityForUserMutation()
  const entries: TrialActivityEntry[] = data?.data ?? []

  if (userId === null) return null

  async function handleDeleteAll() {
    if (userId === null) return
    try {
      await deleteAllForUser(userId).unwrap()
      refetch()
    } catch {
      // Error handled by API
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Trial Activity Log</h2>
            {userEmail && <p className="text-sm text-gray-500 mt-0.5">{userEmail} · user #{userId}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDeleteAll}
              disabled={isDeleting || entries.length === 0}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded border border-red-200 transition-colors"
              title="Delete entire activity log for this account"
            >
              Delete all
            </button>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isLoading}
              className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-800 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-teal-50 transition-colors"
              title="Refresh log"
            >
              <svg
                className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full" />
            </div>
          )}
          {isError && (
            <p className="text-sm text-red-500 text-center py-8">Failed to load activity log.</p>
          )}
          {!isLoading && !isError && entries.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">No activity recorded yet.</p>
          )}
          {!isLoading && !isError && entries.length > 0 && (
            <ol className="relative border-l border-gray-200 ml-3">
              {entries.map((entry, idx) => (
                <li key={entry.id} className="mb-4 ml-6">
                  <span className="absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-teal-100 ring-4 ring-white text-teal-700 text-xs font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-800">{formatActionLabel(entry)}</span>
                    <span className="text-xs text-gray-400 mt-0.5">{formatDateTimeEST(entry.created_at)}</span>
                    {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                      <span className="text-xs text-gray-500 mt-0.5 font-mono bg-gray-50 rounded px-1.5 py-0.5 w-fit">
                        {JSON.stringify(entry.metadata)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-400">
          <span>{entries.length} action{entries.length !== 1 ? 's' : ''} recorded</span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 text-xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

const Onboarding: React.FC = () => {
  const [page, setPage] = useState(1)
  const [flowFilter, setFlowFilter] = useState<string>('')
  const [activityUserId, setActivityUserId] = useState<number | null>(null)
  const [activityUserEmail, setActivityUserEmail] = useState<string | null>(null)
  const limit = 50

  const { data: statsData, isLoading: statsLoading } = useGetOnboardingStatsQuery()
  const { data: tableData, isLoading: tableLoading } = useGetOnboardingTableQuery({
    page,
    limit,
    flow: flowFilter || undefined,
    sortBy: 'created_at',
    sortOrder: 'DESC',
  })

  const stats = statsData?.data
  const rows: OnboardingTableRow[] = tableData?.data ?? []
  const total = tableData?.meta?.total ?? 0
  const totalPages = Math.ceil(total / limit)

  function openActivity(r: OnboardingTableRow) {
    setActivityUserId(r.user_id)
    setActivityUserEmail(r.email)
  }

  return (
    <div className="p-6 w-full max-w-[2400px] mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Onboarding</h1>

      {/* Stats: first number = unique IPs, parenthesis = total submissions including repeated IP */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Metrics by flow <span className="text-gray-500 font-normal text-sm">(unique IP / total)</span></h2>
        {statsLoading ? (
          <div className="animate-pulse flex gap-4">
            <div className="h-32 flex-1 bg-gray-200 rounded-lg" />
            <div className="h-32 flex-1 bg-gray-200 rounded-lg" />
            <div className="h-32 flex-1 bg-gray-200 rounded-lg" />
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-6">
            {(() => {
              const fmt = (m: { unique: number; total: number } | undefined) => m == null ? '0' : `${m.unique} (${m.total})`;
              return (
                <>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Startup</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">RFP submitted:</span> <strong>{fmt(stats.startup.rfpSubmitted)}</strong></li>
                <li><span className="text-gray-600">Team selection submitted:</span> <strong>{fmt(stats.startup.teamSelectionSubmitted)}</strong></li>
                <li><span className="text-gray-600">v0 code created:</span> <strong>{stats.startup.v0CodeCreated}</strong></li>
                <li><span className="text-gray-600">Email registrations:</span> <strong>{fmt(stats.startup.emailRegistrations)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.startup.orgProjectNamed)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Scope</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">RFP submitted:</span> <strong>{fmt(stats.scope.rfpSubmitted)}</strong></li>
                <li><span className="text-gray-600">Team selection submitted:</span> <strong>{fmt(stats.scope.teamSelectionSubmitted)}</strong></li>
                <li><span className="text-gray-600">Additional scope submitted:</span> <strong>{fmt(stats.scope.additionalScopeSubmitted)}</strong></li>
                <li><span className="text-gray-600">Email registrations:</span> <strong>{fmt(stats.scope.emailRegistrations)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.scope.orgProjectNamed)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Reshuffle</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">RFP submitted:</span> <strong>{fmt(stats.reshuffle.rfpSubmitted)}</strong></li>
                <li><span className="text-gray-600">Team selection submitted:</span> <strong>{fmt(stats.reshuffle.teamSelectionSubmitted)}</strong></li>
                <li><span className="text-gray-600">Email registrations:</span> <strong>{fmt(stats.reshuffle.emailRegistrations)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.reshuffle.orgProjectNamed)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Risk Intelligence</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">RFP submitted:</span> <strong>{fmt(stats.riskintelligence?.rfpSubmitted)}</strong></li>
                <li><span className="text-gray-600">Team selection submitted:</span> <strong>{fmt(stats.riskintelligence?.teamSelectionSubmitted)}</strong></li>
                <li><span className="text-gray-600">Email registrations:</span> <strong>{fmt(stats.riskintelligence?.emailRegistrations)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.riskintelligence?.orgProjectNamed)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Plan</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">RFP submitted:</span> <strong>{fmt(stats.plan?.rfpSubmitted)}</strong></li>
                <li><span className="text-gray-600">Team selection submitted:</span> <strong>{fmt(stats.plan?.teamSelectionSubmitted)}</strong></li>
                <li><span className="text-gray-600">Email registrations:</span> <strong>{fmt(stats.plan?.emailRegistrations)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.plan?.orgProjectNamed)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-teal-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-teal-800 mb-3">Trial</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">Sessions started:</span> <strong>{fmt(stats.trial?.rfpSubmitted)}</strong></li>
                <li><span className="text-gray-600">Email registrations:</span> <strong>{fmt(stats.trial?.emailRegistrations)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.trial?.orgProjectNamed)}</strong></li>
                <li><span className="text-gray-600">Projects created:</span> <strong>{fmt(stats.trial?.projectsCreated)}</strong></li>
                <li><span className="text-gray-600">Credits purchased:</span> <strong>{fmt(stats.trial?.creditsPurchased)}</strong></li>
                <li><span className="text-gray-600">Collaborator seats purchased:</span> <strong>{fmt(stats.trial?.collaboratorSeatsPurchased)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-emerald-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-emerald-800 mb-3">Mo</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">Landing page visits:</span> <strong>{fmt(stats.check?.checkLandingPageView)}</strong></li>
                <li><span className="text-gray-600">Sessions started:</span> <strong>{fmt(stats.check?.checkSessionStarted)}</strong></li>
                <li><span className="text-gray-600">Accounts registered (/mo signup):</span> <strong>{fmt(stats.check?.moAccountRegistered)}</strong></li>
                <li><span className="text-gray-600">Slack connected:</span> <strong>{fmt(stats.check?.checkSlackConnected)}</strong></li>
                <li><span className="text-gray-600">GitHub connected:</span> <strong>{fmt(stats.check?.checkGithubConnected)}</strong></li>
                <li><span className="text-gray-600">Credits purchased:</span> <strong>{fmt(stats.check?.checkCreditPurchased)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-indigo-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-indigo-800 mb-3">Motiocomp</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">Landing page visits:</span> <strong>{fmt(stats.motiocomp?.motiocompLandingPageView)}</strong></li>
                <li><span className="text-gray-600">Sessions started:</span> <strong>{fmt(stats.motiocomp?.motiocompSessionStarted)}</strong></li>
                <li><span className="text-gray-600">Accounts registered (/motiocomp signup):</span> <strong>{fmt(stats.motiocomp?.motiocompAccountRegistered)}</strong></li>
                <li><span className="text-gray-600">Tickets compiled (work packs):</span> <strong>{fmt(stats.motiocomp?.motiocompTicketCompiled)}</strong></li>
                <li><span className="text-gray-600">Output checks run:</span> <strong>{fmt(stats.motiocomp?.motiocompOutputCheckRun)}</strong></li>
              </ul>
            </div>
            <div className="bg-white border border-amber-200 rounded-lg shadow-sm p-5">
              <h3 className="font-semibold text-amber-800 mb-3">Pm (Execution)</h3>
              <ul className="space-y-2 text-sm">
                <li><span className="text-gray-600">Landing page visits:</span> <strong>{fmt(stats.pm?.pmLandingPageView)}</strong></li>
                <li><span className="text-gray-600">Accounts registered (/pm signup):</span> <strong>{fmt(stats.pm?.pmAccountRegistered)}</strong></li>
                <li><span className="text-gray-600">Organizations + projects named:</span> <strong>{fmt(stats.pm?.pmOrgProjectNamed)}</strong></li>
                <li><span className="text-gray-600">Deliverables reached:</span> <strong>{fmt(stats.pm?.pmDeliverablesReached)}</strong></li>
              </ul>
            </div>
                </>
              );
            })()}
          </div>
        ) : null}
      </section>

      {/* Feedback table */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Demo feedback</h2>
        <div className="mb-4 flex items-center gap-4">
          <label className="text-sm text-gray-600">Flow</label>
          <select
            value={flowFilter}
            onChange={(e) => { setFlowFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          >
            <option value="">All</option>
            <option value="startup">Startup</option>
            <option value="scope">Scope</option>
            <option value="reshuffle">Reshuffle</option>
            <option value="riskintelligence">Risk Intelligence</option>
            <option value="plan">Plan</option>
            <option value="trial">Trial</option>
            <option value="check">Mo</option>
            <option value="motiocomp">Motiocomp</option>
            <option value="pm">Pm (Execution)</option>
          </select>
        </div>
        {tableLoading ? (
          <div className="animate-pulse h-64 bg-gray-200 rounded-lg" />
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-[2360px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Flow</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Device</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">User ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Resources email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">IP</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Location</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Organization</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Project</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Form result</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Form comments</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Last step</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Agreed ToS</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Email updates</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Loaded demo</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Origin</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Source</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Duration</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Activity</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={19} className="px-4 py-6 text-center text-gray-500 text-sm">No feedback yet.</td>
                    </tr>
                  ) : (
                    rows.map((r) => {
                      const location = [r.ip_city, r.ip_region, r.ip_country].filter(Boolean).join(', ') || '—';
                      const lastStepLabel = (() => {
                        const t = r.last_event_type;
                        if (!t) return '—';
                        if (t === 'rfp_submitted') return 'RFP';
                        if (t === 'team_selection_submitted') return 'Team selection';
                        if (t === 'additional_scope_submitted') return 'Additional scope';
                        if (t === 'org_project_named') return 'Organization+project named';
                        if (t === 'email_registration') return 'Email registration';
                        if (t === 'v0_code_created') return 'v0 code created';
                        if (t === 'demo_feedback') return 'Trial demo feedback';
                        if (t === 'project_created') return 'Project created';
                        if (t === 'credits_purchased') return 'Credits purchased';
                        if (t === 'collaborator_seats_purchased') return 'Collaborator seats purchased';
                        if (t === 'check_session_started') return 'Mo session started';
                        if (t === 'mo_account_registered') return 'Mo: Account registered';
                        if (t === 'check_landing_page_view') return 'Mo: Landing page';
                        if (t === 'check_slack_connected') return 'Mo: Slack connected';
                        if (t === 'check_github_connected') return 'Mo: GitHub connected';
                        if (t === 'check_credit_purchased') return 'Mo: Credit purchased';
                        if (t === 'motiocomp_landing_page_view') return 'Motiocomp: Landing page';
                        if (t === 'motiocomp_landing_ui_action') return 'Motiocomp: Landing UI action';
                        if (t === 'motiocomp_landing_client_error') return 'Motiocomp: Landing error';
                        if (t === 'motiocomp_session_started') return 'Motiocomp session started';
                        if (t === 'motiocomp_account_registered') return 'Motiocomp: Account registered';
                        if (t === 'motiocomp_ticket_compiled') return 'Motiocomp: Ticket compiled';
                        if (t === 'motiocomp_output_check_run') return 'Motiocomp: Output check run';
                        if (t === 'pm_landing_page_view') return 'Pm: Landing page';
                        if (t === 'pm_landing_ui_action') return 'Pm: Landing UI action';
                        if (t === 'pm_landing_client_error') return 'Pm: Landing error';
                        if (t === 'pm_account_registered') return 'Pm: Account registered';
                        if (t === 'pm_org_project_named') return 'Pm: Org + project named';
                        if (t === 'pm_deliverables_reached') return 'Pm: Deliverables reached';
                        return t;
                      })();

                      const truncate = (value: string | null | undefined, max = 20) => {
                        if (!value) return { display: '—', full: null as string | null };
                        if (value.length <= max) return { display: value, full: value };
                        return { display: `${value.slice(0, max)}…`, full: value };
                      };

                      const nameDisplay = truncate(r.user_name ?? undefined, 20);
                      const emailDisplay = truncate(r.email ?? undefined, 20);
                      const resourcesEmailDisplay = truncate(r.resources_email ?? undefined, 20);
                      const locationDisplay = truncate(location === '—' ? null : location, 24);
                      const orgDisplay = truncate(r.organization_name ?? undefined, 20);
                      const projectDisplay = truncate(r.project_name ?? undefined, 20);
                      const lastStepDisplay = truncate(lastStepLabel === '—' ? null : lastStepLabel, 24);
                      const originDisplay = truncate(r.origin ?? undefined, 18);
                      const sourceDisplay = truncate(r.source ?? undefined, 28);
                      const commentsDisplay = truncate(r.comments ?? undefined, 32);

                      const formatDuration = (seconds: number | null | undefined): string => {
                        if (seconds == null || Number.isNaN(seconds)) return '—';
                        const total = Math.max(0, Math.floor(seconds));
                        const hrs = Math.floor(total / 3600);
                        const mins = Math.floor((total % 3600) / 60);
                        const secs = total % 60;
                        if (hrs > 0) {
                          return `${hrs}h ${mins}m`;
                        }
                        if (mins > 0) {
                          return `${mins}m ${secs}s`;
                        }
                        return `${secs}s`;
                      };

                      return (
                      <tr key={r.id} className="bg-white hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{r.flow}</td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[120px] truncate"
                          title={r.device ? r.device.replace(/_/g, ' ') : undefined}
                        >
                          {r.device ? r.device.replace(/_/g, ' ') : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{r.user_id}</td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate"
                          title={nameDisplay.full ?? undefined}
                        >
                          {nameDisplay.display}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate"
                          title={emailDisplay.full ?? undefined}
                        >
                          {emailDisplay.display}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate"
                          title={resourcesEmailDisplay.full ?? undefined}
                        >
                          {resourcesEmailDisplay.display}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 font-mono whitespace-nowrap">{r.ip_address ?? '—'}</td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate"
                          title={locationDisplay.full ?? undefined}
                        >
                          {locationDisplay.display}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate"
                          title={orgDisplay.full ?? undefined}
                        >
                          {orgDisplay.display}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate"
                          title={projectDisplay.full ?? undefined}
                        >
                          {projectDisplay.display}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 capitalize whitespace-nowrap">{r.rating ?? '—'}</td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[220px] truncate"
                          title={commentsDisplay.full ?? undefined}
                        >
                          {commentsDisplay.display}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[220px] truncate"
                          title={lastStepDisplay.full ?? undefined}
                        >
                          {lastStepDisplay.display}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                          {r.agreed_to_terms === 1 ? '✓' : r.agreed_to_terms === 0 ? '✗' : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                          {r.accept_email_updates === 1 ? '✓' : r.accept_email_updates === 0 ? '✗' : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                          {r.loaded_demo === 1 ? 'Demo' : (r.loaded_demo === 0 ? 'Not demo' : '—')}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-gray-700 max-w-[160px] truncate"
                          title={originDisplay.full ?? undefined}
                        >
                          {originDisplay.display}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                          {r.source ? (
                            <a
                              href={r.source}
                              className="text-blue-600 hover:underline max-w-[220px] truncate inline-block align-bottom"
                              target="_blank"
                              rel="noreferrer"
                              title={sourceDisplay.full ?? undefined}
                            >
                              {sourceDisplay.display}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                          {r.flow === 'trial' && r.trial_duration_seconds != null
                            ? formatDuration(r.trial_duration_seconds)
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <button
                            type="button"
                            onClick={() => openActivity(r)}
                            className="px-2.5 py-1 rounded bg-teal-50 border border-teal-200 text-teal-700 text-xs font-medium hover:bg-teal-100 whitespace-nowrap"
                          >
                            Activity log
                          </button>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{formatDateTimeEST(r.created_at)}</td>
                      </tr>
                    ); })
                  )}
                </tbody>
              </table>
            {totalPages > 1 && (
              <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between text-sm">
                <span className="text-gray-600">Total: {total}</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="py-1">Page {page} of {totalPages}</span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Activity log modal */}
      {activityUserId !== null && (
        <ActivityModal
          userId={activityUserId}
          userEmail={activityUserEmail}
          onClose={() => { setActivityUserId(null); setActivityUserEmail(null); }}
        />
      )}
    </div>
  )
}

export default Onboarding
